import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/auth'
import router from '@/router'

interface User {
  id: string
  username: string
  email: string
  role: string
  fullName: string
  firstName: string
  lastName: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  role_label: string | null
  status: number
  last_login_at: string | null
  avatarUrl: string | null
}

const pickText = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
const pickAvatarUrl = (source: Record<string, unknown> | null) => {
  if (!source) {
    return null
  }

  const avatarCandidates: unknown[] = [
    source.avatarUrl,
    source.avatar,
    source.avatar_url,
    source.profileImage,
    source.profileImageUrl,
    source.imageUrl,
  ]

  const found = avatarCandidates.find(item => typeof item === 'string' && item.trim().length > 0)

  return typeof found === 'string' ? found : null
}

const pickFullName = (source: Record<string, unknown> | null) => {
  if (!source) {
    return ''
  }

  const directFullName =
    pickText(source.fullName) ||
    pickText(source.full_name) ||
    pickText(source.displayName)
  if (directFullName) {
    return directFullName
  }

  const firstName = pickText(source.firstName)
  const lastName = pickText(source.lastName)
  return [firstName, lastName].filter(Boolean).join(' ').trim()
}

const buildAuthUser = (rawUser: Record<string, unknown>): User => {
  const fullName = pickFullName(rawUser)
  const avatarUrl = pickAvatarUrl(rawUser)

  return {
    id: String(rawUser.id || rawUser.sub || ''),
    username: pickText(rawUser.username),
    email: pickText(rawUser.email),
    role: pickText(rawUser.role) || 'COMMON',
    fullName,
    firstName: pickText(rawUser.firstName),
    lastName: pickText(rawUser.lastName),
    isActive: typeof rawUser.isActive === 'boolean' ? rawUser.isActive : true,
    createdAt: pickText(rawUser.createdAt) || new Date().toISOString(),
    updatedAt: pickText(rawUser.updatedAt) || new Date().toISOString(),
    role_label: typeof rawUser.role_label === 'string' ? rawUser.role_label : null,
    status: Number(rawUser.status) || 1,
    last_login_at: typeof rawUser.last_login_at === 'string' ? rawUser.last_login_at : null,
    avatarUrl,
  }
}

const persistAuthUser = (rawUser: Record<string, unknown>): User => {
  const normalizedUser = buildAuthUser(rawUser)
  localStorage.setItem('user', JSON.stringify(normalizedUser))
  return normalizedUser
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(localStorage.getItem('token'))
  const refreshTokenValue = ref<string | null>(localStorage.getItem('refreshToken'))
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let isRedirectingToLogin = false
  const PUBLIC_ROUTES = ['/login', '/forgot-password', '/reset-password']

  // Computed
  const isAuthenticated = computed(() => !!token.value)

  // Actions
  const setAuth = (authToken: string, userData: User, refreshTokenStr?: string) => {
    token.value = authToken
    user.value = persistAuthUser(userData as unknown as Record<string, unknown>)
    localStorage.setItem('token', authToken)
    if (refreshTokenStr) {
      refreshTokenValue.value = refreshTokenStr
      localStorage.setItem('refreshToken', refreshTokenStr)
    }

    console.log('setAuth: User data stored', userData)
  }

  const clearAuth = () => {
    token.value = null
    refreshTokenValue.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }

  const redirectToLogin = async (reason = 'session_expired') => {
    if (isRedirectingToLogin) {
      return
    }
    isRedirectingToLogin = true

    clearAuth()

    try {
      if (typeof window === 'undefined') {
        return
      }

      const currentPath = window.location.pathname
      if (!PUBLIC_ROUTES.includes(currentPath)) {
        await router.replace({
          path: '/login',
          query: {
            reason,
          },
        })
      }
    } finally {
      isRedirectingToLogin = false
    }
  }

  const login = async (credentials: { username: string; password: string }) => {
    isLoading.value = true
    error.value = null // 清除之前的错误信息

    try {
      const response = await authService.login(credentials)

      // BFF 模式：响应格式是 {success: true, user: {...}} 或 {success: true, data: {accessToken, ...}, user: {...}}
      // Token 模式：响应格式是 {success: true, data: {accessToken, refreshToken, user: {...}}}
      if (response.success) {
        let userData: User | undefined
        let accessToken: string | null = null
        let refreshToken: string | null = null

        if (response.data?.accessToken && response.data?.user) {
          // Token 模式 - data 包含所有内容
          const data = response.data
          accessToken = data.accessToken
          refreshToken = data.refreshToken
          // 处理后端返回的不完整 user 数据
          const backendUser = data.user
          userData = {
            id: String(backendUser.id),
            username: backendUser.username,
            email: backendUser.email,
            role: backendUser.role || 'COMMON',
            fullName: pickFullName(backendUser as Record<string, unknown>) || '',
            firstName: backendUser.firstName || '',
            lastName: backendUser.lastName || '',
            isActive: backendUser.isActive ?? true,
            createdAt: backendUser.createdAt || new Date().toISOString(),
            updatedAt: backendUser.updatedAt || new Date().toISOString(),
            role_label: null,
            status: 1,
            last_login_at: null,
            avatarUrl: pickAvatarUrl(backendUser as Record<string, unknown>),
          }
        } else if (response.user) {
          // BFF 模式 - 只有 user,token 在服务端 session
          userData = {
            id: String(response.user.sub || response.user.id || ''),
            username: response.user.username || '',
            email: response.user.email || '',
            role: response.user.role || 'COMMON',
            fullName: pickFullName(response.user as unknown as Record<string, unknown>) || '',
            firstName: response.user.firstName || '',
            lastName: response.user.lastName || '',
            isActive: response.user.isActive ?? true,
            createdAt: response.user.createdAt || new Date().toISOString(),
            updatedAt: response.user.updatedAt || new Date().toISOString(),
            role_label: null,
            status: 1,
            last_login_at: null,
            avatarUrl: pickAvatarUrl(response.user as unknown as Record<string, unknown>),
          }
          accessToken = null
          refreshToken = null
        }

        if (userData) {
          // 只在非 BFF 模式下存储 token
          if (accessToken) {
            setAuth(accessToken, userData, refreshToken ?? undefined)
          } else {
            // BFF 模式：只存储用户信息，不存储 token
            user.value = persistAuthUser(userData as unknown as Record<string, unknown>)
            console.log('[Auth Store] BFF mode: User stored (token managed by server)', userData)
            // Debug: 检查 cookie 是否设置
            console.log('[Auth Store] Cookie check:', {
              allCookies: document.cookie,
              hasBffSid: document.cookie.includes('bff_sid'),
              bffSidValue: document.cookie.match(/bff_sid=([^;]+)/)?.[1] || 'not found'
            })
          }

          console.log('[Auth Store] Login successful, attempting navigation to /dashboard')
          try {
            await router.push('/dashboard')
          } catch (navError: any) {
            console.error('[Auth Store] Navigation error:', navError)
          }
          return true
        }
      }

      // 如果到这里说明登录失败
      console.log('Login failed response:', response)
      error.value = response.message || response.error || '登录失败'
      return false
    } catch (err: any) {
      console.error('Login error:', err) // 添加详细日志

      // Extract error message from API response
      if (err.response?.data?.message) {
        error.value = err.response.data.message
      } else if (err.response?.data?.error) {
        error.value = err.response.data.error
      } else if (err.message) {
        error.value = err.message
      } else {
        error.value = '登录失败'
      }
      return false
    } finally {
      isLoading.value = false
    }
  }


  const logout = async () => {
    try {
      await authService.logout(refreshTokenValue.value || undefined)
    } catch (err) {
      console.error('Logout API call failed:', err)
    } finally {
      clearAuth()
      await router.push('/login')
    }
  }

  const checkAuth = async () => {
    if (!token.value) {
      return false
    }

    try {
      const response = await authService.getProfile()
      if (response.success) {
        user.value = persistAuthUser(response.data as Record<string, unknown>)
        return true
      } else {
        clearAuth()
        return false
      }
    } catch (err) {
      clearAuth()
      return false
    }
  }

  const refreshToken = async () => {
    try {
      if (!refreshTokenValue.value) {
        return false
      }
      const response = await authService.refreshToken(refreshTokenValue.value)
      if (response.success && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data
        // Only update tokens, keep existing user data
        token.value = accessToken
        refreshTokenValue.value = newRefreshToken
        localStorage.setItem('token', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        return true
      }
      return false
    } catch (err) {
      return false
    }
  }

  // Initialize auth state from localStorage
  const initializeAuth = () => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    console.log('initializeAuth: storedToken:', !!storedToken)
    console.log('initializeAuth: storedUser:', storedUser)

    // 检查是否为 BFF 模式
    const isBffMode = (import.meta as any).env?.VITE_DEV_USE_BFF === 'true'

    if (isBffMode) {
      // BFF 模式：只需要恢复用户数据
      if (storedUser) {
        try {
          const rawUser = JSON.parse(storedUser)
          user.value = persistAuthUser(rawUser)
          console.log('initializeAuth (BFF): parsed user:', user.value)
        } catch (err) {
          console.error('initializeAuth: Failed to parse stored user', err)
          clearAuth()
        }
      } else if (storedToken) {
        // 清理历史 token，避免 BFF 模式下误判和错误的 WebSocket 连接
        clearAuth()
      }
    } else {
      // Token 模式：需要 token 和 user 都存在
      if (storedToken && storedUser) {
        try {
          token.value = storedToken
          const rawUser = JSON.parse(storedUser)
          user.value = persistAuthUser(rawUser)
          console.log('initializeAuth (Token): parsed user:', user.value)

          if (!user.value.fullName) {
            void checkAuth()
          }
        } catch (err) {
          console.error('initializeAuth: Failed to parse stored user', err)
          clearAuth()
        }
      } else if (storedToken || storedUser) {
        // 存在不完整登录态（常见于历史版本或异常中断），统一清理
        clearAuth()
      }
    }
  }

  return {
    // State
    token,
    user,
    isLoading,
    error,

    // Computed
    isAuthenticated,

    // Actions
    login,
    logout,
    checkAuth,
    refreshToken,
    initializeAuth,
    redirectToLogin,
    clearAuth,
  }
})
